import { useCallback, useEffect, useState, RefObject } from 'react';

import { useResizeObserver } from '@ui';

import * as css from './SwingCardList.css';

const CARD_OFFSET = css.CARD_LIST_GAP + css.HORIZONTAL_CARD_WIDTH;

type SliderHookProps<T> = { ref: RefObject<T | null>; count: number };

const getLeftOffset = (index: number, remainderWidth = 0) => {
    return CARD_OFFSET * index - remainderWidth;
};

export const useSlider = <T extends HTMLDivElement>({ ref, count }: SliderHookProps<T>) => {
    const [index, setIndex] = useState(0);
    const { width = 0 } = useResizeObserver({ ref });

    // Calculate the amount of cards that fully fit within the slider
    const contains = Math.floor(width / CARD_OFFSET);

    // Since the list can contain a partial card, we need to calculate the remainder
    // We need to add css.CARD_LIST_GAP to width, since the last card does not have a gap element.
    const remainderWidth = width - contains * CARD_OFFSET + css.CARD_LIST_GAP;

    // We don't want to iterate to the last card, only to the last list section where the last card is visible
    const lastIndex = count > contains ? count - contains : 0;

    const isLast = index === lastIndex;
    const isFirst = index === 0;

    // Remainder width is an offset which is done at the end of the list.
    // However, when the list is empty, we are technically at the end of the list but not need it.
    // This is why `&& !!count` check was added.
    const leftOffset = isLast && isFirst ? 0 : getLeftOffset(index, isLast ? remainderWidth : 0);

    const showLeftButton = index !== 0;
    const showRightButton = !isLast;

    const prev = useCallback(() => {
        if (isFirst) return;
        setIndex((idx) => idx - 1);
    }, [isFirst]);

    const next = useCallback(() => {
        if (isLast) return;
        setIndex((idx) => idx + 1);
    }, [isLast]);

    return {
        prev,
        next,
        // Inner slider offset, when card changes, this offset dictates how much the slier should move
        leftOffset,
        showLeftButton,
        showRightButton,
    };
};

type SliderSwipeHookProps<T> = {
    ref: RefObject<T | null>;
    next: () => void;
    prev: () => void;
};

export const useSliderSwipe = <T extends HTMLDivElement>({ ref, next, prev }: SliderSwipeHookProps<T>) =>
    useEffect(() => {
        let startX: number | null = null;

        const onMouseDown = (e: MouseEvent) => {
            startX = e.clientX;
        };

        const onMouseUp = () => {
            startX = null;
        };

        const onMouseMove = (e: MouseEvent) => {
            if (startX === null) {
                return;
            }

            const mouseOffsetSinceStartX = startX ? e.clientX - startX : 0;
            /**
             * We multiply mouse offset by 2, since moving the mouse a distance equal to half of offset is enough
             * to proc card slider change.
             */
            const indexChange = Math.floor(mouseOffsetSinceStartX / CARD_OFFSET);
            if (indexChange > 0) {
                prev();
                startX = null;
                return;
            }
            if (indexChange < 0) {
                next();
                startX = null;
                return;
            }
        };

        ref.current?.addEventListener('mousedown', onMouseDown);

        /**
         * We use window event, because the user while dragging the mouse
         * can exit the element and we need to react to mouse up event
         * even if it's outside the slider component
         */
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ref.current?.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [next, prev, ref]);
